import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "px-4 first:pl-0 last:pr-0"
}
const _hoisted_2 = {
  key: 0,
  class: "overflow-hidden rounded-2xl border border-[#e7ddcc] bg-white"
}
const _hoisted_3 = {
  key: 0,
  class: "border-t border-[#e7ddcc] py-1"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 1,
  class: "mt-4"
}
const _hoisted_6 = { class: "relative mt-4 flex h-4 items-center select-none" }
const _hoisted_7 = { class: "pointer-events-none absolute inset-x-0 h-[3px] rounded-full bg-[#e7ddcc]" }
const _hoisted_8 = ["title"]
const _hoisted_9 = ["max", "value"]
const _hoisted_10 = { class: "w-full mt-2 h-20 overflow-hidden" }
const _hoisted_11 = ["src", "alt"]

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading
  from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const variantIdx = ""


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3StonesPosition',
  props: {
    groupIdx: {}
  },
  emits: ["patch"],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emit = __emit;

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3Grooves.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, so getItemVariants loads/patches the variant of
 * the item that is actually being configured right now (single item), while the
 * "configure all" feature (if later needed for a shared/set value) still has the
 * correct configureAll flag available via the constructor.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * position details - stones.<groupIdx>.object and stones.<groupIdx>.object_arrangement
 *
 * Mirrors itemV3Grooves.vue's "activeGroove.object"/"activeGroove.object_arrangement":
 * the raw "stones.<n>.object" / "stones.<n>.object_arrangement" variants for the stone
 * group given via props.groupIdx.
 */

const position = ref({
  "object": {},
  "object_arrangement": {}
} as any)

const getPositionData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    position.value.object = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".object")
    position.value.object_arrangement = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".object_arrangement")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getPositionData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getPositionData(props.groupIdx)
}, {deep: true})

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  value = (typeof value == "string") ? value : value.idx
  emit('patch', {
    action: actionIdx,
    value: value,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * position - object (Bezug)
 */

const showObjectDropdown = ref(false)

const currentObject = computed(() => (position.value.object[0]?.values || []).find((v: any) => v.match))

/**
 * position - object arrangement (Ausrichtung) - step slider
 *
 * Rendered as a step slider (transferred from itemV3Grooves.vue): the title of the
 * currently selected position is shown above the track, and every entry in
 * position.value.object_arrangement[0].values is one step. Some steps may be marked
 * as not allowed/sendable by the API - those stay visible on the track but can't be
 * dragged onto/selected (see isArrangementDisabled).
 */

const arrangementValues = computed(() => position.value.object_arrangement[0]?.values || [])

const isArrangementDisabled = (value: any) => {
  return value?.disabled === true || value?.sendable === false || value?.selectable === false || value?.available === false
}

const arrangementIdx = computed(() => {
  const idx = arrangementValues.value.findIndex((v: any) => v.match)
  return idx === -1 ? 0 : idx
})

// local slider position, so dragging the handle only updates the UI - the api call is
// only sent once the user drops (releases) the slider, see onArrangementRangeChange
const sliderArrangementIdx = ref(0)

watch(arrangementIdx, (idx) => {
  sliderArrangementIdx.value = idx
}, {immediate: true})

const arrangementStepPercent = (idx: number) => {
  if (arrangementValues.value.length < 2) return 0
  return (idx / (arrangementValues.value.length - 1)) * 100
}

const arrangementSliderPercent = computed(() => arrangementStepPercent(sliderArrangementIdx.value))

const currentArrangement = computed(() => arrangementValues.value[sliderArrangementIdx.value])

// The image bucket only keeps one folder per object shape ("ring" / "disc"), not one per
// disc idx (e.g. "disc-1") - so a "disc-<id>" object idx is normalized down to "disc".
const arrangementImageObject = computed(() => {
  const idx = currentObject.value?.idx?.toString() || ''
  return idx.toLowerCase().startsWith('disc') ? 'disc' : idx
})

const arrangementImageUrl = computed(() => {
  if (!arrangementImageObject.value || !currentArrangement.value?.idx) return ''
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/object_arrangement/"
      + arrangementImageObject.value + "/256/" + currentArrangement.value.idx + ".webp"
})

const onArrangementRangeInput = (event: Event) => {
  sliderArrangementIdx.value = parseInt((event.target as HTMLInputElement).value)
}

const onArrangementRangeChange = (event: Event) => {
  if (!position.value.object_arrangement[0]) return
  const idx = parseInt((event.target as HTMLInputElement).value)
  const value = arrangementValues.value[idx]
  if (value && !isArrangementDisabled(value)) {
    handleValuePatch(position.value.object_arrangement[0].idx, value)
  } else {
    // dropped on a step that isn't allowed/sendable - snap back to the current selection
    sliderArrangementIdx.value = arrangementIdx.value
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (position.value.object[0] || position.value.object_arrangement[0])
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (position.value.object[0])
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("button", {
                  type: "button",
                  class: "flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-xs text-parameter-title transition-colors hover:bg-[#f3f0e9]",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (showObjectDropdown.value = !showObjectDropdown.value))
                }, [
                  _createElementVNode("span", null, _toDisplayString(currentArrangement.value?.title) + " " + _toDisplayString(currentObject.value?.title), 1),
                  (_openBlock(), _createElementBlock("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    class: _normalizeClass(["size-3.5 shrink-0 transition-transform", showObjectDropdown.value ? 'rotate-180' : '']),
                    viewBox: "0 0 24 24",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round"
                  }, [...(_cache[1] || (_cache[1] = [
                    _createElementVNode("polyline", { points: "6 9 12 15 18 9" }, null, -1)
                  ]))], 2))
                ]),
                (showObjectDropdown.value)
                  ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(position.value.object[0].values, (stoneObject, stoneObjectIdx) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: stoneObjectIdx,
                          class: _normalizeClass(["mx-1.5 cursor-pointer rounded-xl px-2.5 py-2 text-xs transition-colors hover:bg-[#f3f0e9]", stoneObject.match ? 'bg-[#e7ddcc] font-medium text-parameter-title' : 'text-parameter-text']),
                          onClick: ($event: any) => {handleValuePatch(position.value.object[0].idx, stoneObject); showObjectDropdown.value = false}
                        }, _toDisplayString(currentArrangement.value?.title) + " " + _toDisplayString(stoneObject.title), 11, _hoisted_4))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (position.value.object_arrangement[0])
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", {
                      class: "h-full rounded-full bg-brand-main",
                      style: _normalizeStyle({ width: arrangementSliderPercent.value + '%' })
                    }, null, 4)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(arrangementValues.value, (stoneObjectArrangement, stoneObjectArrangementIdx) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: stoneObjectArrangementIdx,
                      title: stoneObjectArrangement.title,
                      class: _normalizeClass(["pointer-events-none absolute top-1/2 size-1.5 rounded-full", isArrangementDisabled(stoneObjectArrangement) ? 'bg-[#c9c2b3]' : 'border border-brand-main bg-white']),
                      style: _normalizeStyle({ left: arrangementStepPercent(stoneObjectArrangementIdx as number) + '%', transform: 'translate(-50%, -50%)' })
                    }, null, 14, _hoisted_8))
                  }), 128)),
                  _createElementVNode("div", {
                    class: "pointer-events-none absolute top-1/2 size-4 rounded-full bg-brand-main shadow-md",
                    style: _normalizeStyle({ left: arrangementSliderPercent.value + '%', transform: 'translate(-50%, -50%)' })
                  }, null, 4),
                  _createElementVNode("input", {
                    type: "range",
                    class: "absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0",
                    min: "0",
                    max: arrangementValues.value.length - 1,
                    step: "1",
                    value: sliderArrangementIdx.value,
                    onInput: onArrangementRangeInput,
                    onChange: onArrangementRangeChange
                  }, null, 40, _hoisted_9)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  (arrangementImageUrl.value)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: arrangementImageUrl.value,
                        alt: currentArrangement.value?.title || '',
                        class: "aspect-square w-full max-w-44 -rotate-90 object-contain relative -top-1/2 mx-auto"
                      }, null, 8, _hoisted_11))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      : (_openBlock(), _createBlock(ItemV3StonesParameterLoading, { key: 1 }))
  ]))
}
}

})